/**
 * /m — phone-first shell for field techs (v0.32.0).
 *
 * Deliberately minimal: sticky top bar (back / brand / desktop-escape /
 * logout), no desktop Navbar, single-column content with big touch
 * targets. Reuses the exact same session (cookies + api client) as the
 * desktop app; small screens are auto-redirected here from /locations
 * unless they've opted out via the "Desktop" link.
 */
import { ArrowLeft, LogOut, Monitor } from "lucide-react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { ReauthModal } from "../../auth/ReauthModal";
import { useLogout } from "../../auth/SessionContext";

/** localStorage flag: user explicitly chose the desktop app on this
 * device — the small-screen auto-redirect honours it. */
export const DESKTOP_OPT_OUT_KEY = "boxman-desktop-opt-out";

export function isSmallScreen(): boolean {
  return typeof window !== "undefined" && window.innerWidth < 640;
}

export function desktopOptedOut(): boolean {
  try {
    return localStorage.getItem(DESKTOP_OPT_OUT_KEY) === "1";
  } catch {
    return false;
  }
}

export function MobileShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useLogout();
  const atRoot = location.pathname === "/m";

  return (
    <div className="flex min-h-screen flex-col bg-slate-950 text-slate-100">
      <header className="sticky top-0 z-10 flex items-center gap-2 border-b border-slate-800 bg-slate-900 px-3 py-2">
        {!atRoot && (
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Back"
            className="inline-flex h-11 w-11 items-center justify-center rounded text-slate-300 hover:bg-slate-800"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
        )}
        <span className="flex-1 truncate text-sm font-semibold">
          Boxman{atRoot ? " — locations" : ""}
        </span>
        <button
          type="button"
          onClick={() => {
            try {
              localStorage.setItem(DESKTOP_OPT_OUT_KEY, "1");
            } catch {
              /* private mode — ignore */
            }
            navigate("/locations");
          }}
          className="inline-flex h-11 items-center gap-1 rounded px-2 text-xs text-slate-400 hover:bg-slate-800 hover:text-slate-200"
          title="Switch to the desktop app (remembered on this device)"
        >
          <Monitor className="h-4 w-4" />
          Desktop
        </button>
        <button
          type="button"
          onClick={() => logout.mutate()}
          aria-label="Log out"
          className="inline-flex h-11 w-11 items-center justify-center rounded text-slate-400 hover:bg-slate-800 hover:text-red-300"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </header>
      <main className="mx-auto w-full max-w-screen-sm flex-1 px-3 py-3">
        <Outlet />
      </main>
      <ReauthModal />
    </div>
  );
}
